import http from '../services/http';
import { auth } from '../firebase';

const ticketingAdminOptions = async () => {
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error('Sign in to manage ticketing.');
  return { headers: { 'auth-token': token } };
};

const scoringResultEmailRequest = async (operation, data) => {
  const options = await ticketingAdminOptions();
  try {
    const response = await http.post(`/api/v1/apcs/scoring-result-emails/${operation}`, data,
      { ...options, timeout: 90000 });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Result email request failed.');
  }
};

const handleErrors = (err /* path  payload */) => {
  // const errorMessageKey = err.response?.data?.error.Message;
  const errorMessageKey = err.response;
  return { error: errorMessageKey };
};

const getRequest = async (path, options) => {
  try {
    return await http.get(path, options);
  } catch (err) {
    return handleErrors(err);
  }
};

const postRequest = async (path, payload, options) => {
  try {
    const res = await http.post(path, payload, options);
    return res;
  } catch (err) {
    return handleErrors(err);
  }
};

const postBlobRequest = async (path, payload) => {
  try {
    const res = await http.postBlob(path, payload);
    return res;
  } catch (err) {
    return handleErrors(err);
  }
};

const putRequest = async (path, payload) => {
  try {
    const res = await http.put(path, payload);
    return res;
  } catch (err) {
    return handleErrors(err);
  }
};

const patchRequest = async (path, payload) => {
  try {
    return await http.patch(path, payload);
  } catch (err) {
    return handleErrors(err);
  }
};

const deleteRequest = async (path, options) => {
  try {
    return await http.delete(path, undefined, options);
  } catch (err) {
    return handleErrors(err);
  }
};

// eslint-disable-next-line import/no-anonymous-default-export
export default {
  registrantAttendance: {
    preview: async (data) => {
      const options = await ticketingAdminOptions();
      try { return (await http.post('/api/v1/apcs/registrant-attendance/preview', data, { ...options, timeout: 90000 })).data; }
      catch (error) { throw new Error(error.response?.data?.message || error.message || 'Attendance preview failed.'); }
    },
    apply: async (data) => {
      const options = await ticketingAdminOptions();
      try { return (await http.post('/api/v1/apcs/registrant-attendance/apply', data, { ...options, timeout: 90000 })).data; }
      catch (error) { throw new Error(error.response?.data?.message || error.message || 'Attendance import failed.'); }
    },
  },
  auth: {
    login: (payload) => postRequest('/admin/login', payload),
  },
  payment: {
    createPayment: (data) => postRequest(`/api/v1/apcs/createPayment`, data)
  },
  register: {
    submitRegistration: (data) => postRequest(`/api/v1/apcs/register`, data),
  },
  email: {
    previewScoringResults: (data) => scoringResultEmailRequest('preview', data),
    sendScoringResults: (data) => scoringResultEmailRequest('send', data),
    testScoringResults: (data) => scoringResultEmailRequest('test', data),
    sendEmail: (data) => postRequest(`/api/v1/apcs/sendEmail`, data),
    sendEmailJuryReminderDummy: (data) => postRequest(`/api/v1/apcs/sendEmailJuryReminderDummy`, data),
    sendEmailAnnouncement: (data) => postRequest(`/api/v1/apcs/sendEmailAnnouncement`, data),
    sendEmailAnnouncementJson: (data) => postRequest(`/api/v1/apcs/sendEmailAnnouncementJson`, data),
    sendEmailSessionWinner: (data) => postRequest(`/api/v1/apcs/sendEmailSessionWinner`, data),
    sendGeneralSeatingEmail: (data) => postRequest(`/api/v1/apcs/sendGeneralSeatingEmail`, data),
    sendEmailPaymentRequest: (data) => postRequest(`/api/v1/apcs/sendEmailPaymentRequest`, data),
    sendEmailPaymentRequestPaynow: (data) => postRequest(`/api/v1/apcs/sendEmailPaymentRequestPaynow`, data),
    sendEmailNotifyApcs: (data) => postRequest(`/api/v1/apcs/sendEmailNotifyApcs`, data),
    sendEmailNotifyBulkUpdateRegistrant: (data) => postRequest(`/api/v1/apcs/sendEmailNotifyBulkUpdateRegistrant`, data),
    sendEmailStageReschedule: (data) => postRequest(`/api/v1/apcs/sendEmailStageReschedule`, data),
    sendEmailGalaConcertUpdate: (data) => postRequest(`/api/v1/apcs/sendEmailGalaConcertUpdate`, data),
    sendEmailGalaWinnerAnnouncement: (data) => postRequest(`/api/v1/apcs/sendEmailGalaWinnerAnnouncement`, data),
    sendEmailPerformanceInvitation: (data) => postRequest(`/api/v1/apcs/sendEmailPerformanceInvitation`, data),
    sendEmailSoundOfAsia2026Invite: (data) => postRequest(`/api/v1/apcs/sendEmailSoundOfAsia2026Invite`, data),
    sendEmailPaymentInfoOptions: (data) => postRequest(`/api/v1/apcs/sendEmailPaymentInfoOptions`, data),
    sendEmailGalaConcert2026ConfirmationFromCSV: (data) => postRequest(`/api/v1/apcs/sendEmailGalaConcert2026ConfirmationFromCSV`, data),
    resendConfirmationEmail: (data) => postRequest(`/api/v1/apcs/resendConfirmationEmail`, data),
  },
  galery: {
    getGalery: (eventName) => getRequest(`/api/v1/apcs/getGaleries?eventName=${eventName}`),
    getVideos: () => getRequest(`/api/v1/apcs/getVideos`)
  },
  home: {
    getSponsors: () => getRequest(`/api/v1/apcs/getSponsors`)
  },
  jury: {
    createJury: (data) => postRequest(`/api/v1/apcs/createJury`, data),
    updateJury: (uid, data) => putRequest(`/api/v1/apcs/updateJury/${uid}`, data),
    deleteJury: (uid) => deleteRequest(`/api/v1/apcs/deleteJury/${uid}`),
  },
  aws: {
    postSignedUrl: (directoryname, fileName, fileType) => postRequest(`/api/v1/apcs/signed-url-images?directoryname=${directoryname}&fileName=${fileName}&fileType=${fileType}`),
    // Initiate multipart upload
    initiateMultipartUpload: (directoryname, fileName, fileType) =>
      postRequest(`/api/v1/apcs/initiateMultipartUpload`, {
        directoryname,
        fileName,
        fileType
      }),

    // Get signed URL for each part
    getPartUploadUrl: (directoryname, fileName, uploadId, partNumber) =>
      postRequest(`/api/v1/apcs/getPartUploadUrl`, {
        directoryname,
        fileName,
        uploadId,
        partNumber
      }),

    // Complete multipart upload
    completeMultipartUpload: (directoryname, fileName, uploadId, parts) =>
      postRequest(`/api/v1/apcs/completeMultipartUpload`, {
        directoryname,
        fileName,
        uploadId,
        parts
      }),
      
    // Abort multipart upload
    abortMultipartUpload: (directoryname, fileName, uploadId) =>
      postRequest(`/api/v1/apcs/abortMultipartUpload`, {
        directoryname,
        fileName,
        uploadId
      }),

    downloadFiles: (files) => postBlobRequest(`/api/v1/apcs/download-files-aws`, files),
    downloadAllFiles: (files) => postBlobRequest(`/api/v1/apcs/download-all-files-aws`, files),
    getPublicVideoLinkAws: (s3Link) => postRequest(`/api/v1/apcs/getPublicVideoLinkAws`, s3Link),
  },
  paymentGatewayFlow: {
    create: (data) => postRequest(`/api/v1/apcs/paymentIntegration/bookings`, data),
    checkStatus: (bookingId) => postRequest(`/api/v1/apcs/paymentIntegration/bookings/status?bookingId=${bookingId}`),
  },
  payment: {
    createRegistrationInvoice: (data) => postRequest(`/api/v1/apcs/payment/createInvoice`, data),
    getRegistrantStatus: (id) => getRequest(`/api/v1/apcs/payment/registrant-status/${id}`),
  },
  bookings: {
    saveSeatBookProfileInfo: (data) => postRequest(`/api/v1/apcs/saveSeatBookProfileInfo`, data),
    sendSeatBookingEmail: (data) => postRequest(`/api/v1/apcs/sendSeatBookingEmail`, data),
    verifySeatToken: (data) => postRequest(`/api/v1/apcs/verify-seat-token`, data),
    confirmSeatSelection: (data) => postRequest(`/api/v1/apcs/confirm-seats`, data), // ini yang dipake setelah registrant pilih seat
  },
  tickets: {
    verify: (eventName) => getRequest(`/api/v1/apcs/getGaleries?eventName=${eventName}`),
  },
  registrant: {
    migrateEventId: (data) => postRequest(`/api/v1/apcs/migrateEventId`, data),
  },
  publicTicket: {
    redemption: async (action, data) => {
      let options;
      try { options = await ticketingAdminOptions(); }
      catch (error) { error.retryable = true; throw error; }
      try {
        const result = (await http.post(`/api/v1/apcs/public-ticket/admin/redemption/${action}`, data, options)).data;
        if (action === 'claim' && (!result?.claimId || !result?.redemptionId || !Number.isSafeInteger(result.revision) || result.revision < 1)) {
          throw new Error('The claim response was incomplete. Retry the saved request to confirm its result.');
        }
        return result;
      } catch (error) {
        const failure = new Error(error.response?.data?.message || error.message || 'Ticket redemption request failed.');
        failure.uncertain = !error.response || error.response.status >= 500 || error.response.status === 408;
        failure.retryable = [401, 403, 429].includes(error.response?.status);
        throw failure;
      }
    },
    orchestraAdmin: (action, data, authToken) => postRequest(
      `/api/v1/apcs/public-ticket/admin/orchestra/${action}`, data,
      { headers: { 'auth-token': authToken } },
    ),
    getEventData: () => getRequest(`/api/v1/apcs/public-ticket/event-data`),
    getSeats: (venueId, sessionId) => getRequest(`/api/v1/apcs/public-ticket/seats?venueId=${venueId}&sessionId=${sessionId}`),
    getEligibleWinners: (buyerType) => getRequest(`/api/v1/apcs/public-ticket/eligible-winners${buyerType === 'public' ? '?buyerType=public' : ''}`),
    createBooking: async (data) => {
      try {
        // Paper.id creation and the holding email can exceed the shared 30-second timeout.
        return await http.post('/api/v1/apcs/public-ticket/booking', data, { timeout: 90000 });
      } catch (error) {
        // No HTTP response means the server may still have created the booking/invoice.
        throw new Error(error.response?.data?.message
          || 'We could not confirm your booking response. Your booking may already be reserved. Please retry with the same details or contact APCS before starting a new booking.');
      }
    },
    getBookingStatus: (bookingId) => getRequest(`/api/v1/apcs/public-ticket/booking-status/${bookingId}`),
    resendPublicTicketEmail: (data) => postRequest(`/api/v1/apcs/public-ticket/resend-email`, data),
    releaseBooking: (data, authToken) => postRequest(
      `/api/v1/apcs/public-ticket/admin/release-booking`,
      data,
      { headers: { 'auth-token': authToken } },
    ),
    markManualPaid: (bookingId, authToken) => postRequest(
      `/api/v1/apcs/public-ticket/admin/mark-manual-paid`,
      { bookingId },
      { headers: { 'auth-token': authToken } },
    ),
    resendManualInstructions: (bookingId, authToken) => postRequest(
      `/api/v1/apcs/public-ticket/admin/resend-manual-instructions`,
      { bookingId },
      { headers: { 'auth-token': authToken } },
    ),
    assignSeats: async (bookingId, seatIds) => {
      try {
        const response = await http.post(`/api/v1/apcs/public-ticket/admin/assign-seats`,
          { bookingId, seatIds }, await ticketingAdminOptions());
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || error.message || 'Seat assignment failed.');
      }
    },
    generateSeats: async (eventId, venueId, sessionIds) => {
      try {
        const response = await http.post(`/api/v1/apcs/public-ticket/admin/seats/generate`,
          { eventId, venueId, sessionIds }, { ...(await ticketingAdminOptions()), timeout: 120000 });
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || error.message || 'Seat generation failed.');
      }
    },
  },
  systemSettings: {
    getGlobalSettings: () => getRequest(`/api/v1/apcs/systemSettings/global`),
    updateGlobalSettings: (data) => postRequest(`/api/v1/apcs/systemSettings/global`, data),
  },
  venueSettings: {
    updateMetadata: async (eventId, venueId, data) => {
      try {
        const response = await http.post(`/api/v1/apcs/venue-settings/${encodeURIComponent(eventId)}/${encodeURIComponent(venueId)}/metadata`,
          data, await ticketingAdminOptions());
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || error.message || 'Failed to save venue.');
      }
    },
  },
  competitionPlanning: {
    getState: async (eventId) => getRequest(`/api/v1/apcs/competition-planning/${eventId}`, await ticketingAdminOptions()),
    saveGroup: async (eventId, data) => postRequest(`/api/v1/apcs/competition-planning/${eventId}/groups`, data, await ticketingAdminOptions()),
    saveDraft: async (eventId, expectedRevision, groups) => postRequest(`/api/v1/apcs/competition-planning/${eventId}/draft`, { expectedRevision, groups }, await ticketingAdminOptions()),
    deleteGroup: async (eventId, groupId) => deleteRequest(`/api/v1/apcs/competition-planning/${eventId}/groups/${groupId}`, await ticketingAdminOptions()),
    saveSlot: async (eventId, expectedRevision, slot) => postRequest(`/api/v1/apcs/competition-planning/${eventId}/slots`, { expectedRevision, slot }, await ticketingAdminOptions()),
    deleteSlot: async (eventId, slotId, expectedRevision) => postRequest(`/api/v1/apcs/competition-planning/${eventId}/slots/${slotId}/delete`, { expectedRevision }, await ticketingAdminOptions()),
    preview: async (eventId) => postRequest(`/api/v1/apcs/competition-planning/${eventId}/preview`, {}, await ticketingAdminOptions()),
    publish: async (eventId, revision) => postRequest(`/api/v1/apcs/competition-planning/${eventId}/publish`, { expectedRevision: revision }, await ticketingAdminOptions()),
    markReady: async (eventId) => postRequest(`/api/v1/apcs/competition-planning/${eventId}/ready`, {}, await ticketingAdminOptions()),
  },
  session: {
    saveAssignments: async (data) => postRequest(`/api/v1/apcs/saveSessionAssignments`, data, await ticketingAdminOptions()),
    getAssignments: (eventId) => getRequest(`/api/v1/apcs/getSessionAssignments/${eventId}`),
  },
};
